/* Tailwind directives - these are processed by the Tailwind CSS compiler */
/* These may show as lint errors in some editors but are valid in projects using Tailwind */
@tailwind base;
@tailwind components;
@tailwind utilities;

html { font-family: var(--font-sans); }

:root {
  /* Colors */
  --color-primary: #0a7568; /* brand teal (AA on white) */
  --color-primary-light: #5fc9bb; /* teal-300 */
  --color-primary-dark: #06564c; /* teal-700 */

  /* ===== Brand design tokens (shadcn semantic layer) — light theme ===== */
  --background: 0 0% 100%;
  --foreground: 222 47% 11%;
  --card: 0 0% 100%;
  --card-foreground: 222 47% 11%;
  --popover: 0 0% 100%;
  --popover-foreground: 222 47% 11%;
  --primary: 174 84% 24%;
  --primary-foreground: 0 0% 100%;
  --secondary: 210 40% 96%;
  --secondary-foreground: 222 47% 11%;
  --muted: 210 40% 96%;
  --muted-foreground: 215 16% 47%;
  --accent: 174 55% 95%;
  --accent-foreground: 174 84% 20%;
  --destructive: 0 72% 51%;
  --destructive-foreground: 0 0% 100%;
  --border: 214 32% 91%;
  --input: 214 32% 91%;
  --ring: 174 80% 32%;
  --radius: 0.5rem;
  --brand: 172 67% 58%;
  --font-sans: "IBM Plex Sans", ui-sans-serif, system-ui, sans-serif;
  --font-mono: "IBM Plex Mono", ui-monospace, Menlo, monospace;
  
  --color-secondary: #6b7280; /* gray-500 */
  --color-secondary-light: #d1d5db; /* gray-300 */
  --color-secondary-dark: #374151; /* gray-700 */
  
  --color-success: #10b981; /* green-500 */
  --color-success-light: #6ee7b7; /* green-300 */
  --color-success-dark: #047857; /* green-700 */
  
  --color-danger: #ef4444; /* red-500 */
  --color-danger-light: #fca5a5; /* red-300 */
  --color-danger-dark: #b91c1c; /* red-700 */
  
  --color-warning: #f59e0b; /* amber-500 */
  --color-warning-light: #fcd34d; /* amber-300 */
  --color-warning-dark: #b45309; /* amber-700 */
  
  --color-info: #3b82f6; /* blue-500 */
  --color-info-light: #93c5fd; /* blue-300 */
  --color-info-dark: #1d4ed8; /* blue-700 */
  
  --color-error: #ef4444; /* red-500 */
  --color-error-light: #fca5a5; /* red-300 */
  --color-error-dark: #b91c1c; /* red-700 */
  
  --color-background: #ffffff;
  --color-background-light: #f9fafb; /* gray-50 */
  --color-background-dark: #1f2937; /* gray-800 */
  --color-surface: #f9fafb; /* gray-50 */
  
  --color-text: #111827; /* gray-900 */
  --color-text-secondary: #6b7280; /* gray-500 */
  --color-text-disabled: #9ca3af; /* gray-400 */
  
  /* Overlay colors */
  --color-overlay-light: rgba(0, 0, 0, 0.05);
  --color-overlay-medium: rgba(0, 0, 0, 0.1);
  --color-overlay-dark: rgba(0, 0, 0, 0.2);
  --color-hover-overlay: rgba(0, 0, 0, 0.05);
  --color-focus-overlay: rgba(0, 0, 0, 0.1);
  
  /* Spacing */
  --spacing-xs: 0.25rem; /* 4px */
  --spacing-sm: 0.5rem;  /* 8px */
  --spacing-md: 1rem;    /* 16px */
  --spacing-lg: 1.5rem;  /* 24px */
  --spacing-xl: 2rem;    /* 32px */
  
  /* Border radius */
  --radius-sm: 0.125rem; /* 2px */
  --radius-md: 0.375rem; /* 6px */
  --radius-lg: 0.5rem;   /* 8px */
  --radius-xl: 0.75rem;  /* 12px */
  
  /* Font sizes */
  --font-size-xs: 0.75rem;  /* 12px */
  --font-size-sm: 0.875rem; /* 14px */
  --font-size-md: 1rem;     /* 16px */
  --font-size-lg: 1.125rem; /* 18px */
  --font-size-xl: 1.25rem;  /* 20px */
  --font-size-2xl: 1.5rem;  /* 24px */
  
  /* Font weights */
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;
  
  /* Shadows */
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  
  /* Transitions */
  --transition-speed-fast: 150ms;
  --transition-speed-normal: 200ms;
  --transition-speed-slow: 300ms;
  
  /* Border widths */
  --border-width-sm: 1px;
  --border-width-md: 2px;
  --border-width-lg: 4px;
}

/* Custom component classes */
.btn {
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-medium);
  transition: all 0.2s ease;
  font-size: 0.625rem;
  white-space: nowrap;
}

@media (min-width: 480px) {
  .btn {
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
  }
}

@media (min-width: 640px) {
  .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }
}

@media (min-width: 768px) {
  .btn {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
  }
}

@media (min-width: 1024px) {
  .btn {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 0.875rem;
  }
}

.btn-primary {
  background-color: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--color-primary-dark);
}

.btn-success {
  background-color: var(--color-success);
  color: white;
}

.btn-success:hover {
  background-color: var(--color-success);
  filter: brightness(90%);
}

.btn-danger {
  background-color: var(--color-danger);
  color: white;
}

.btn-danger:hover {
  background-color: var(--color-danger);
  filter: brightness(90%);
}

.input-field {
  border: 1px solid var(--color-secondary-light);
  border-radius: var(--radius-md);
  padding: var(--spacing-sm);
  width: 100%;
  font-size: var(--font-size-md);
  color: var(--color-text);
}

.input-field:focus {
  border-color: var(--color-primary);
  outline: none;
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.file-input-btn {
  background-color: var(--color-primary-light);
  color: var(--color-primary-dark);
  font-weight: var(--font-weight-semibold);
}

.file-input-btn:hover {
  background-color: var(--color-primary);
  color: white;
}

.file-input-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.card {
  background-color: var(--color-background);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  padding: var(--spacing-lg);
}

.error-text {
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.success-text {
  color: var(--color-success);
  font-size: var(--font-size-sm);
  margin-top: var(--spacing-xs);
}

/* Dropdown component styles */
.dropdown-container {
  margin-top: var(--spacing-sm);
}

@media (min-width: 640px) {
  .dropdown-container {
    margin-top: var(--spacing-md);
  }
}

.dropdown-label {
  display: block;
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-xs);
}

@media (min-width: 640px) {
  .dropdown-label {
    font-size: var(--font-size-sm);
  }
}

.dropdown-select {
  margin-top: var(--spacing-xs);
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-secondary-light);
  border-radius: var(--radius-md);
  width: 100%;
  font-size: 0.75rem;
  color: var(--color-text);
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem;
  padding-right: 2rem;
}

@media (min-width: 640px) {
  .dropdown-select {
    padding: var(--spacing-sm);
    font-size: 0.875rem;
    padding-right: 2.5rem;
  }
}

@media (min-width: 768px) {
  .dropdown-select {
    font-size: var(--font-size-md);
  }
}

.dropdown-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.dropdown-select option {
  padding: var(--spacing-sm);
  font-size: 0.875rem;
}

@media (min-width: 768px) {
  .dropdown-select option {
    font-size: var(--font-size-md);
  }
}

/* Checkbox styles */
.checkbox-input {
  width: 1rem;
  height: 1rem;
  accent-color: var(--color-primary);
  cursor: pointer;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .checkbox-input {
    width: 1.25rem;
    height: 1.25rem;
  }
}

.checkbox-label {
  font-size: 0.75rem;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  color: var(--color-text);
  margin-left: 0.375rem;
}

@media (min-width: 640px) {
  .checkbox-label {
    font-size: 0.875rem;
    margin-left: var(--spacing-sm);
  }
}

@media (min-width: 768px) {
  .checkbox-label {
    font-size: var(--font-size-md);
  }
}

/* File preview styles */
.file-preview-container {
  margin-bottom: var(--spacing-sm);
  background-color: var(--color-background-light);
  padding: 0.5rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-secondary-light);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (min-width: 640px) {
  .file-preview-container {
    margin-bottom: var(--spacing-md);
    padding: var(--spacing-md);
  }
}

.file-preview-item {
  margin-bottom: 0.375rem;
}

@media (min-width: 640px) {
  .file-preview-item {
    margin-bottom: var(--spacing-sm);
  }
}

.file-preview-filename {
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  margin-bottom: var(--spacing-xs);
  font-size: 0.75rem;
}

@media (min-width: 640px) {
  .file-preview-filename {
    font-size: var(--font-size-sm);
  }
}

.file-preview-content {
  font-family: monospace;
  font-size: 0.625rem;
  white-space: pre-wrap;
  word-break: break-all;
  color: var(--color-text-secondary);
}

@media (min-width: 640px) {
  .file-preview-content {
    font-size: 0.75rem;
  }
}

@media (min-width: 768px) {
  .file-preview-content {
    font-size: var(--font-size-sm);
  }
}

.delimiter-highlight {
  color: var(--color-error);
  font-weight: var(--font-weight-bold);
}

/* Table styles */
.table-container {
  margin-bottom: var(--spacing-lg);
  width: 100%;
  overflow: hidden;
}

.table-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-sm);
  color: var(--color-text);
}

@media (min-width: 640px) {
  .table-title {
    font-size: var(--font-size-xl);
  }
}

.table-wrapper {
  max-height: 300px;
  overflow: auto;
  border: 1px solid var(--color-secondary-light);
  border-radius: var(--radius-md);
  -webkit-overflow-scrolling: touch;
}

@media (min-width: 640px) {
  .table-wrapper {
    max-height: 400px;
  }
}

@media (min-width: 1024px) {
  .table-wrapper {
    max-height: 500px;
  }
}

/* Page and message container styles */
.page-container {
  position: relative;
  min-height: 100vh;
  background-color: var(--color-background);
}

.messages-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
}

.messages-wrapper {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.main-container {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: var(--spacing-lg);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
}

.language-selector {
  margin-left: var(--spacing-md);
}

/* Loading indicator styles */
.loading-container {
  margin-top: var(--spacing-md);
  text-align: center;
}

.loading-spinner {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 2px solid transparent;
  border-bottom-color: var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: var(--spacing-sm);
  color: var(--color-text-secondary);
}

/* Button container styles */
.button-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

@media (min-width: 640px) {
  .button-container {
    gap: var(--spacing-md);
    margin-top: var(--spacing-md);
  }
}

.button-container button {
  min-width: auto;
  height: auto;
  padding: 0.375rem 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

@media (min-width: 480px) {
  .button-container button {
    padding: 0.5rem 1rem;
  }
}

@media (min-width: 768px) {
  .button-container button {
    min-width: 120px;
    padding: var(--spacing-sm) var(--spacing-md);
  }
}

/* DataTable styles */
.data-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
}

.data-table {
  min-width: 100%;
  background-color: var(--color-white);
  border: 1px solid var(--color-border);
  margin-bottom: var(--spacing-md);
  border-collapse: collapse;
  font-size: 0.75rem;
}

@media (min-width: 640px) {
  .data-table {
    font-size: 0.875rem;
  }
}

@media (min-width: 1024px) {
  .data-table {
    font-size: 1rem;
  }
}

/* Processed data table styles */
.processed-data-container .data-table {
  background-color: #f0fdf4; /* Light green background */
  border: 1px solid #86efac; /* Green border */
}

.processed-data-container .data-table-header {
  background-color: #dcfce7; /* Slightly darker green for headers */
  border-bottom: 1px solid #86efac;
  color: #166534; /* Dark green text */
  font-weight: var(--font-weight-bold);
}

.processed-data-container .data-table-row-even {
  background-color: #f0fdf4; /* Light green */
}

.processed-data-container .data-table-row-odd {
  background-color: #dcfce7; /* Slightly darker green */
}

.data-table-header {
  padding: 0.375rem 0.5rem;
  border-bottom: 1px solid var(--color-border);
  background-color: var(--color-background-light);
  text-align: left;
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

@media (min-width: 640px) {
  .data-table-header {
    padding: var(--spacing-sm) var(--spacing-md);
  }
}

.data-table-cell {
  padding: 0.375rem 0.5rem;
  border-bottom: 1px solid var(--color-border);
  text-align: left;
  white-space: nowrap;
}

@media (min-width: 640px) {
  .data-table-cell {
    padding: var(--spacing-sm) var(--spacing-md);
  }
}

.data-table-row-even {
  background-color: var(--color-white);
}

.data-table-row-odd {
  background-color: var(--color-background-alt);
}

.empty-data-message {
  font-weight: var(--font-weight-light);
  color: var(--color-text-secondary);
  padding: var(--spacing-md);
}

/* Error Message styles */
.error-message {
  position: fixed;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  max-width: calc(100% - 1rem);
  width: auto;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  transition: all 300ms ease-in-out;
}

@media (min-width: 480px) {
  .error-message {
    top: var(--spacing-sm);
    gap: var(--spacing-sm);
    max-width: 400px;
    padding: 0.625rem 1rem;
  }
}

@media (min-width: 640px) {
  .error-message {
    top: var(--spacing-md);
    max-width: 500px;
    padding: var(--spacing-md);
  }
}

.error-message.visible {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.error-message.hidden {
  opacity: 0;
  transform: translateX(-50%) translateY(-0.5rem);
}

.error-message-default {
  background-color: #fee2e2;
  color: #7f1d1d;
  border: 1px solid var(--color-error);
  font-weight: 500;
}

.error-message-dark {
  background-color: var(--color-background-dark);
  color: #fecaca;
  border: 1px solid var(--color-error);
  font-weight: 500;
}

.error-message-light {
  background-color: #ffffff;
  color: #b91c1c;
  border: 1px solid var(--color-error);
  font-weight: 500;
}

.error-icon {
  height: 1rem;
  width: 1rem;
  flex-shrink: 0;
  color: var(--color-error);
}

@media (min-width: 640px) {
  .error-icon {
    height: 1.25rem;
    width: 1.25rem;
  }
}

.error-text {
  flex-grow: 1;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-medium);
}

@media (min-width: 480px) {
  .error-text {
    font-size: 0.75rem;
  }
}

@media (min-width: 640px) {
  .error-text {
    font-size: var(--font-size-sm);
  }
}

.error-close-button {
  flex-shrink: 0;
  border-radius: 50%;
  padding: 0.125rem;
  transition: background-color var(--transition-speed-normal);
}

@media (min-width: 640px) {
  .error-close-button {
    padding: var(--spacing-xs);
  }
}

.error-close-button:hover {
  background-color: var(--color-hover-overlay);
}

.error-close-button:focus {
  outline: none;
  box-shadow: 0 0 0 var(--border-width-md) var(--color-error), 0 0 0 var(--border-width-lg) var(--color-focus-overlay);
}

.close-icon {
  height: var(--spacing-lg);
  width: var(--spacing-lg);
}

/* Success Message styles */
.success-message {
  position: fixed;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  max-width: calc(100% - 1rem);
  width: auto;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  transition: all 300ms ease-in-out;
}

@media (min-width: 480px) {
  .success-message {
    top: var(--spacing-sm);
    gap: var(--spacing-sm);
    max-width: 400px;
    padding: 0.625rem 1rem;
  }
}

@media (min-width: 640px) {
  .success-message {
    top: var(--spacing-md);
    max-width: 500px;
    padding: var(--spacing-md);
  }
}

.success-message.visible {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.success-message.hidden {
  opacity: 0;
  transform: translateX(-50%) translateY(-0.5rem);
}

.success-message-default {
  background-color: #d1fae5;
  color: #065f46;
  border: 1px solid var(--color-success);
  font-weight: 500;
}

.success-message-blue {
  background-color: #dbeafe;
  color: #1e40af;
  border: 1px solid var(--color-info);
  font-weight: 500;
}

.success-message-yellow {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid var(--color-warning);
  font-weight: 500;
}

.success-icon {
  height: 1rem;
  width: 1rem;
  flex-shrink: 0;
  color: var(--color-success);
}

@media (min-width: 640px) {
  .success-icon {
    height: 1.25rem;
    width: 1.25rem;
  }
}

.success-text {
  flex-grow: 1;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-medium);
}

@media (min-width: 480px) {
  .success-text {
    font-size: 0.75rem;
  }
}

@media (min-width: 640px) {
  .success-text {
    font-size: var(--font-size-sm);
  }
}

.success-close-button {
  flex-shrink: 0;
  border-radius: 50%;
  padding: 0.125rem;
  transition: background-color var(--transition-speed-normal);
}

@media (min-width: 640px) {
  .success-close-button {
    padding: var(--spacing-xs);
  }
}

.success-close-button:hover {
  background-color: var(--color-hover-overlay);
}

.success-close-button:focus {
  outline: none;
  box-shadow: 0 0 0 var(--border-width-md) var(--color-success), 0 0 0 var(--border-width-lg) var(--color-focus-overlay);
}

.close-icon {
  width: 0.875rem;
  height: 0.875rem;
}

@media (min-width: 640px) {
  .close-icon {
    width: 1rem;
    height: 1rem;
  }
}

/* DelimiterInput Component */
.delimiter-container {
  margin-bottom: var(--spacing-lg);
}

.delimiter-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin-bottom: var(--spacing-sm);
}

.input-container {
  position: relative;
}

.delimiter-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-lg);
  border: 2px solid var(--color-secondary-light);
  transition: all 0.2s ease;
  outline: none;
  font-size: 0.75rem;
}

@media (min-width: 640px) {
  .delimiter-input {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: 0.875rem;
  }
}

@media (min-width: 768px) {
  .delimiter-input {
    font-size: 1rem;
  }
}

.delimiter-input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.delimiter-input.error {
  border-color: var(--color-danger-light);
  box-shadow: 0 0 0 2px var(--color-danger-light);
}

.icon-container {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding-right: var(--spacing-sm);
  pointer-events: none;
}

.error-icon {
  height: 20px;
  width: 20px;
  color: var(--color-danger);
}

.success-icon {
  height: 20px;
  width: 20px;
  color: var(--color-success);
}

.delimiter-error-message {
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--color-danger);
  display: block;
  position: static;
}

.help-text {
  margin-top: var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* ================================================
   UI POLISH - Animation & Interaction Utilities
   ================================================ */

/* Hover lift effect for buttons */
.hover-lift {
  @apply transition-all duration-200;
}
.hover-lift:hover {
  @apply -translate-y-0.5 shadow-lg;
}
.hover-lift:active {
  @apply translate-y-0;
}

/* Card interactive hover */
.card-interactive {
  @apply transition-all duration-200;
}
.card-interactive:hover {
  @apply shadow-lg border-blue-300;
}

/* Hover glow effect */
.hover-glow {
  @apply transition-shadow duration-200;
}
.hover-glow:hover {
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.15);
}

/* Focus ring utility */
.focus-ring {
  @apply focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none;
}

/* List item hover */
.list-item-hover {
  @apply transition-colors duration-150;
}
.list-item-hover:hover {
  @apply bg-blue-50;
}

/* Progress bar shine animation */
.progress-shine {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: progress-shine 2s linear infinite;
}

@keyframes progress-shine {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Subtle pulse for active states */
.pulse-active {
  animation: pulse-subtle 2s ease-in-out infinite;
}

@keyframes pulse-subtle {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Fade in animation */
.animate-fade-in {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* Slide up animation for modals */
.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* Check bounce for validation items */
.check-bounce {
  animation: check-bounce 0.3s ease-out;
}

@keyframes check-bounce {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* Scale in animation */
.animate-scale-in {
  animation: scale-in 0.2s ease-out;
}

@keyframes scale-in {
  0% { transform: scale(0.95); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

/* Backdrop blur for modals */
.backdrop-blur-modal {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* Number transition for metrics */
.number-transition {
  transition: all 0.3s ease-out;
}

/* Selected state glow */
.selected-glow {
  @apply ring-2 ring-blue-100 shadow-lg;
}

/* Button loading spinner */
.btn-spinner {
  @apply inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full;
  animation: spin 0.8s linear infinite;
}

/* ================================================
   ADDITIONAL UI/UX UTILITIES
   ================================================ */

/* Smooth scrollbar styling */
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.8);
}

/* Input group styling */
.input-group {
  @apply relative transition-all duration-200;
}

.input-group:focus-within {
  @apply ring-2 ring-blue-200 rounded-lg;
}

/* Floating label animation */
.floating-label {
  @apply absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-all duration-200 text-gray-500;
}

.floating-label.active,
input:focus + .floating-label,
input:not(:placeholder-shown) + .floating-label {
  @apply text-xs -translate-y-6 text-blue-600;
}

/* Card elevation levels */
.elevation-1 {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);
}

.elevation-2 {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
}

.elevation-3 {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04);
}

.elevation-4 {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.03);
}

/* Gradient text */
.gradient-text {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Stagger animation delays */
.stagger-1 { animation-delay: 50ms; }
.stagger-2 { animation-delay: 100ms; }
.stagger-3 { animation-delay: 150ms; }
.stagger-4 { animation-delay: 200ms; }
.stagger-5 { animation-delay: 250ms; }

/* Better focus visible styling */
.focus-visible-ring:focus-visible {
  @apply outline-none ring-2 ring-blue-500 ring-offset-2;
}

/* Disabled state styling */
.disabled-overlay {
  @apply relative;
}

.disabled-overlay::after {
  content: '';
  @apply absolute inset-0 bg-white/50 cursor-not-allowed;
}

/* Badge pulse for notifications */
.badge-pulse {
  @apply relative;
}

.badge-pulse::after {
  content: '';
  @apply absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Ripple effect for buttons */
.ripple {
  @apply relative overflow-hidden;
}

.ripple::after {
  content: '';
  @apply absolute inset-0 pointer-events-none;
  background: radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%);
  transform: scale(0);
  opacity: 0;
}

.ripple:active::after {
  transform: scale(2);
  opacity: 1;
  transition: transform 0.3s, opacity 0.3s;
}

/* Skeleton loading base */
.skeleton {
  @apply relative overflow-hidden bg-gray-200 rounded;
}

.skeleton::after {
  content: '';
  @apply absolute inset-0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Tooltip styling */
.tooltip {
  @apply invisible opacity-0 absolute z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded transition-all duration-200;
}

.tooltip-trigger:hover .tooltip {
  @apply visible opacity-100;
}

/* Status dot indicators */
.status-dot {
  @apply w-2 h-2 rounded-full;
}

.status-dot.online { @apply bg-green-500; }
.status-dot.offline { @apply bg-gray-400; }
.status-dot.busy { @apply bg-yellow-500; }
.status-dot.error { @apply bg-red-500; }

.status-dot.pulse {
  animation: status-pulse 2s infinite;
}

@keyframes status-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Icon button styling */
.icon-btn {
  @apply p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500;
}

.icon-btn.sm { @apply p-1.5; }
.icon-btn.lg { @apply p-3; }

/* Divider with text */
.divider-text {
  @apply flex items-center text-gray-500 text-sm;
}

.divider-text::before,
.divider-text::after {
  content: '';
  @apply flex-1 h-px bg-gray-300;
}

/* A11Y-W12-4: WCAG 2.5.5 (Target Size, Level AAA).
   Inside main content areas on mobile, enforce 44×44 minimum touch
   target size on interactive controls. Excludes navbar (outside main). */
@media (max-width: 768px) {
  [role="main"] button,
  [role="main"] a,
  [role="main"] select,
  [role="main"] input[type="checkbox"],
  [role="main"] input[type="radio"] {
    min-height: 44px;
    min-width: 44px;
  }

  /* For icon-only buttons, ensure the padding still gives the hit area */
  [role="main"] button.icon-only {
    padding: 12px;
  }
}

/* Reusable utility for explicit opt-in (outside main or special cases). */
.touch-target-44 {
  min-height: 44px;
  min-width: 44px;
}

.divider-text::before { @apply mr-3; }
.divider-text::after { @apply ml-3; }